import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import Spinner from '../components/Spinner';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 0.5rem;
  text-align: center;
  background: linear-gradient(to right, #ffffff 20%, #4B63D3 40%, #4B63D3 60%, #ffffff 80%);
  background-size: 200% auto;
  color: #000;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shine 4s linear infinite;

  @keyframes shine {
    to {
      background-position: 200% center;
    }
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 400px;
`;

const LoginForm = styled.form`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  width: 100%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Input = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};

  &:focus {
    outline: none;
    border-color: #4B63D3;
    box-shadow: 0 0 0 2px rgba(75, 99, 211, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  margin-top: 1rem;
  background: ${props => props.disabled 
    ? 'rgba(75, 99, 211, 0.5)' 
    : 'linear-gradient(135deg, #4B63D3 0%, #84CEEB 100%)'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(75, 99, 211, 0.4)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
  min-height: 1.2rem;
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      console.log('Successfully logged in user:', user.uid);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <Title>Cosmos</Title>
      <LoginContainer>
        <Logo />
        <LoginForm onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            aria-label="Email"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            aria-label="Password"
          />
          <ErrorMessage>
            {error && (
              error.code === 'auth/user-not-found'
                ? 'No account found with this email'
                : error.code === 'auth/wrong-password'
                ? 'Incorrect password'
                : 'An error occurred during login'
            )}
          </ErrorMessage>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="16px" />
                Logging in...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </LoginForm>
      </LoginContainer>
    </PageContainer>
  );
};

export default Login;